import posthog from 'posthog-js'
import { createSignal, type Accessor } from 'solid-js'

/**
 * Thin adapter around `posthog-js`. This is the ONLY file in the codebase that
 * imports `posthog-js` — everything else depends on this module.
 *
 * Scope: flags only. Pageview capture, autocapture, and session recording are
 * all disabled because Mixpanel is the analytics tool for analytics-frontend.
 */

export type PosthogInitArgs = {
  readonly key: string
  readonly host: string
}

export type PosthogFlagsMap = Readonly<Record<string, boolean>>

export type IdentifyArgs = {
  readonly id: string
  readonly email: string
}

/** PostHog Group Analytics group type — matches Chorus-frontend's convention. */
const POSTHOG_GROUP_TYPE = 'artist'

const [posthogFlags, setPosthogFlags] = createSignal<PosthogFlagsMap>({})
let initialized = false
let lastGroupSlug: string | undefined

function asBooleanMap(variants: Readonly<Record<string, boolean | string>>): PosthogFlagsMap {
  const out: Record<string, boolean> = {}
  for (const [key, value] of Object.entries(variants)) {
    // PostHog can return `true`, `false`, or a string variant name.
    // Treat any truthy value as enabled for our boolean-only use case.
    out[key] = value === true || (typeof value === 'string' && value.length > 0)
  }
  return out
}

function flagsEqual(a: PosthogFlagsMap, b: PosthogFlagsMap): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

function applyFlags(variants: Readonly<Record<string, boolean | string>>): void {
  const next = asBooleanMap(variants)
  // Skip the signal write when nothing actually changed — otherwise every
  // posthog.group() call (which fires onFeatureFlags) would invalidate the
  // resolver memo and re-render every useFeature consumer.
  if (flagsEqual(posthogFlags(), next)) return
  setPosthogFlags(next)
}

export function initPosthogBridge({ key, host }: PosthogInitArgs): void {
  if (initialized) return
  initialized = true

  posthog.init(key, {
    api_host: host,
    capture_pageview: false,
    autocapture: false,
    disable_session_recording: true,
    loaded: () => {
      // Use the module-level `posthog` reference to call getFlagVariants —
      // the `loaded` callback parameter is typed as PostHogInterface which
      // exposes featureFlags as a data Record, not the class with methods.
      applyFlags(posthog.featureFlags.getFlagVariants() as Readonly<Record<string, boolean | string>>)
    }
  })

  posthog.onFeatureFlags((_flagKeys, variants) => {
    applyFlags(variants as Readonly<Record<string, boolean | string>>)
  })
}

export function posthogIdentify({ id, email }: IdentifyArgs): void {
  if (!initialized) return
  posthog.identify(id, { email })
}

/**
 * Register the current session's "artist" group with PostHog so feature
 * flags targeted at specific artists via Group Analytics evaluate correctly.
 *
 * Bails out when the slug matches the most recent call — re-emitting the
 * same group fires a /decide network request and an onFeatureFlags callback
 * for nothing.
 *
 * Calling posthog.group() with a new slug implicitly reloads feature flags,
 * so a separate reload call is not needed.
 */
export function posthogSetArtistGroup(slug: string): void {
  if (!initialized || slug === lastGroupSlug) return
  lastGroupSlug = slug
  posthog.group(POSTHOG_GROUP_TYPE, slug, { name: slug })
}

export function usePosthogFlagsSignal(): Accessor<PosthogFlagsMap> {
  return posthogFlags
}

// ---------------------------------------------------------------------------
// Test-only helpers
// ---------------------------------------------------------------------------

/**
 * Resets module-level state so each test starts with a clean slate.
 * Must not be called from production code.
 */
export function __resetPosthogBridgeForTests(): void {
  initialized = false
  lastGroupSlug = undefined
  setPosthogFlags({})
}
